import { useCallback, useEffect, useRef } from 'react';
import { EARCON_COOLDOWN_MS, EARCON_EVENT_TO_SOUND, type EarconEvent } from '@/lib/earcons';
import { usePlaySounds } from '@/hooks/useSoundPlayer';

export function useEarcons() {
  const playSound = usePlaySounds();
  const lastPlayedRef = useRef<Partial<Record<EarconEvent, number>>>({});

  const playEarcon = useCallback(
    (event: EarconEvent) => {
      const cooldown = EARCON_COOLDOWN_MS[event] ?? 0;
      const now = Date.now();
      const lastPlayedAt = lastPlayedRef.current[event] ?? 0;

      if (cooldown > 0 && now - lastPlayedAt < cooldown) {
        return;
      }

      lastPlayedRef.current[event] = now;
      playSound(EARCON_EVENT_TO_SOUND[event]);
    },
    [playSound]
  );

  return { playEarcon };
}

export function useSidebarEarcons(sidebarStates: Record<string, boolean>) {
  const { playEarcon } = useEarcons();
  const prevStatesRef = useRef<Record<string, boolean>>({});
  const didInitRef = useRef(false);

  useEffect(() => {
    if (!didInitRef.current) {
      prevStatesRef.current = { ...sidebarStates };
      didInitRef.current = true;
      return;
    }

    const keys = new Set([
      ...Object.keys(prevStatesRef.current),
      ...Object.keys(sidebarStates),
    ]);

    let openedCount = 0;
    let closedCount = 0;

    keys.forEach((key) => {
      const prevValue = Boolean(prevStatesRef.current[key]);
      const nextValue = Boolean(sidebarStates[key]);

      if (!prevValue && nextValue) openedCount += 1;
      if (prevValue && !nextValue) closedCount += 1;
    });

    if (openedCount > 0) {
      playEarcon('sidebarOpen');
    } else if (closedCount > 0) {
      playEarcon('sidebarClose');
    }

    prevStatesRef.current = { ...sidebarStates };
  }, [playEarcon, sidebarStates]);
}
